import { useScriptTag } from '@vueuse/core'
import getEnvironment from '@/utils/env'
import { onConsentChange, hasConsent, removeCookies } from './onetrust'

declare global {
  interface Window {
    dataLayer?: unknown[]
  }
}

// Preserved from legacy ada-customer-ui google_analytics.js (used as `G-${ID}`).
// This plugin has no ada-app-ui counterpart — Google Analytics only exists in
// the customer portal and must not be dropped.
const GOOGLE_ANALYTICS_ID = import.meta.env.VITE_GOOGLE_ANALYTICS_ID ?? ''

const gaScript = useScriptTag(`https://www.googletagmanager.com/gtag/js?id=G-${GOOGLE_ANALYTICS_ID}`, undefined, {
  immediate: false,
})

// Self-register against the consent registry (import side effect)
onConsentChange('analytics', {
  onAccepted: startGoogleAnalytics,
  onDenied: stopGoogleAnalytics,
})

// The official gtag snippet pushes the `arguments` object — gtag.js silently
// ignores plain arrays, which breaks every command (no init, no cookies).
// The rest parameter is kept only for the typed call signature.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function gtag(..._args: unknown[]): void {
  window.dataLayer = window.dataLayer ?? []
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments)
}

function startGoogleAnalytics(): void {
  const environment = getEnvironment()
  const forceEnable = import.meta.env.VITE_TEST_EXTERNAL_SCRIPTS === 'true'

  // Only enable in production and test environments (unless forced)
  if (!forceEnable && (environment == null || !['production', 'test'].includes(environment.id))) {
    return
  }
  if (!hasConsent('analytics')) {
    return
  }

  gaScript
    .load()
    .then(() => {
      gtag('consent', 'update', { analytics_storage: 'granted' })
      gtag('js', new Date())
      gtag('config', `G-${GOOGLE_ANALYTICS_ID}`)
    })
    .catch((err: unknown) => {
      console.error('Google Analytics failed to load', err)
    })
}

function stopGoogleAnalytics(): void {
  gtag('consent', 'update', { analytics_storage: 'denied' })
  gaScript.unload()
  removeCookies(['_ga', `_ga_${GOOGLE_ANALYTICS_ID}`])
}
